// src/screens/Pagamento/Pagamento.js

import React, { useState, useMemo, useEffect } from 'react';
import { useLocation, useNavigate, useParams, Link } from 'react-router-dom';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebaseconfig';
import { useAuth } from '../../hooks/useAuth';
import { FaLock, FaUser, FaCreditCard, FaCheckCircle } from 'react-icons/fa';
import "./Pagamento.css";

// Mapa de temas para cada aplicativo
import logoCalcLeite from "../../assets/calcleite.png";
import logoCalcCafe from "../../assets/calccafe.png";
import logoCalcBov from "../../assets/calcbov.png";
import logoCalcAgro from "../../assets/calcagro.png";

const appThemes = {
    calcleite: { color: '#1e90ff', name: 'CalcLeite', logo: logoCalcLeite },
    calccafe:  { color: '#6f4e37', name: 'CalcCafé', logo: logoCalcCafe },
    calcbov:   { color: '#4CAF50', name: 'CalcBov', logo: logoCalcBov },
    calcagro:  { color: '#FFC107', name: 'CalcAgro', logo: logoCalcAgro },
};

const Pagamento = () => {
  const stripe = useStripe();
  const elements = useElements();
  const { state } = useLocation();
  const { appKey } = useParams();
  const navigate = useNavigate();
  const { user, userData } = useAuth();

  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [cardHolderName, setCardHolderName] = useState(userData?.nomeCompleto || '');
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const { plan } = state || {};
  const theme = useMemo(() => appThemes[appKey] || { color: '#355e3b', name: 'App', logo: null }, [appKey]);
  
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements || !user || processing) return;
    
    setProcessing(true);
    setError(null);
    
    const cardElement = elements.getElement(CardElement);
    
    const { error: paymentMethodError, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
      billing_details: { name: cardHolderName, email: user.email },
    });

    if (paymentMethodError) {
      setError("Dados do cartão inválidos. Por favor, verifique e tente novamente.");
      console.error("Stripe validation error:", paymentMethodError);
      setProcessing(false);
      return;
    }

    try {
      const idToken = await user.getIdToken();
      const response = await fetch("https://backendcustosagro-e51a.onrender.com/create-subscription", {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}`},
        body: JSON.stringify({
          email: user.email, name: cardHolderName, uid: user.uid,
          paymentMethodId: paymentMethod.id, priceId: plan.stripe_id, app: appKey
        }),
      });

      const { success, data, error: serverError } = await response.json();
      if (!success) throw new Error(serverError || "Falha ao criar assinatura.");

      if (data.status === 'active' || data.status === 'trialing') {
        const userDocRef = doc(db, 'usuarios', user.uid);
        const subscriptionData = {
          subscriptionId: data.subscriptionId, plan: plan.formatted_name, status: data.status,
          currentPeriodEnd: data.current_period_end ? new Date(data.current_period_end * 1000).toISOString() : null,
          trialEnd: data.trial_end ? new Date(data.trial_end * 1000).toISOString() : null,
        };
        await setDoc(userDocRef, {
          premium: true, [appKey]: true,
          subscriptions: { ...userData?.subscriptions, [appKey]: subscriptionData },
          ultimaAtualizacao: serverTimestamp()
        }, { merge: true });
        
        setShowSuccessModal(true);
      } else if (data.clientSecret) {
        const { error: confirmError } = await stripe.confirmCardPayment(data.clientSecret);
        if (confirmError) throw new Error(confirmError.message);
        setShowSuccessModal(true);
      }
    } catch (err) {
      console.error("Erro completo no pagamento:", err);
      setError("Ocorreu um erro ao processar seu pagamento. Por favor, tente novamente mais tarde ou contate o suporte.");
    } finally {
      setProcessing(false);
    }
  };

  // Auto-close modal and navigate after 5 seconds
  useEffect(() => {
    if (showSuccessModal) {
      const timer = setTimeout(() => {
        setShowSuccessModal(false);
        navigate(`/${appKey}/app`);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showSuccessModal, appKey, navigate]);

  const cardElementOptions = {
    style: {
      base: { fontSize: '16px', color: '#333', '::placeholder': { color: '#aab7c4' } },
      invalid: { color: '#fa755a', iconColor: '#fa755a' },
    },
    hidePostalCode: true
  };
  
  if (!plan) {
    return (
        <div className="pagamento-container">
            <div className="pagamento-card-error">
                <h2>Plano não selecionado</h2>
                <p>Ocorreu um erro. Por favor, volte e selecione um plano.</p>
                <Link to="/assinar" className="pagamento-button" style={{ backgroundColor: theme.color }}>Voltar para Apps</Link>
            </div>
        </div>
    );
  }

  const themeColorStyle = {
    '--theme-color': theme.color,
    '--theme-color-light': `${theme.color}20`
  };

  return (
    <div className="pagamento-container" style={themeColorStyle}>
      {showSuccessModal && (
        <div className="success-modal-overlay">
          <div className="success-modal">
            <div className="success-check">
              <FaCheckCircle size={80} color="#4CAF50" />
            </div>
            <h3>Assinatura Ativada!</h3>
            <p>Você ganhou acesso completo ao {theme.name}. Pode continuar pelo site ou voltar ao app.</p>
            <div className="progress-bar-container">
              <div className="progress-bar"></div>
            </div>
            <p className="modal-timer">Redirecionando em 5 segundos...</p>
          </div>
        </div>
      )}
      <div className="pagamento-grid">

        <div className="pagamento-details-col">
          {theme.logo && <img src={theme.logo} alt={`Logo ${theme.name}`} className="pagamento-app-logo" />}
          <h2>Finalizar Assinatura</h2>
          <p className="pagamento-subtitle">Você está adquirindo o plano <strong style={{color: theme.color}}>{plan.formatted_name}</strong> para o <strong>{theme.name}</strong>.</p>
          
          <div className="plano-info">
            <h3>Resumo do Pedido</h3>
            <div className="plano-info-row">
              <span>Plano</span>
              <span>{plan.formatted_name}</span>
            </div>
            <div className="plano-info-row">
              <span>Valor</span>
              <span>{plan.formatted_price}/{plan.formatted_interval}</span>
            </div>
            {plan.trial_days > 0 && 
              <div className="plano-info-row trial">
                <span>Período de Teste</span>
                <span>{plan.trial_days} dias grátis</span>
              </div>
            }
            <div className="plano-info-total">
              <span>Total</span>
              <strong>{plan.formatted_price}</strong>
            </div>
          </div>
        </div>

        <div className="pagamento-form-col">
          <div className="pagamento-card">
            <form onSubmit={handleSubmit}>
                <div className="pagamento-input-group">
                    <label htmlFor="cardHolderName"><FaUser /> Nome no Cartão</label>
                    <input 
                        id="cardHolderName"
                        type="text" 
                        value={cardHolderName}
                        onChange={(e) => setCardHolderName(e.target.value)}
                        placeholder="Nome completo como no cartão"
                        required
                    />
                </div>
                
                <div className="pagamento-input-group">
                    <label><FaCreditCard /> Dados do Cartão de Crédito</label>
                    <div className="card-element-wrapper">
                        <CardElement options={cardElementOptions} />
                    </div>
                </div>
                
                {error && <div className="pagamento-error">{error}</div>}
                
                <button type="submit" disabled={processing || !stripe} className="pagamento-button">
                    {processing ? 'Processando...' : (plan.trial_days > 0 ? 'Iniciar Teste Grátis' : 'Assinar e Pagar')}
                </button>
            </form>
            <footer className="pagamento-footer">
                <FaLock />
                <span>Pagamento seguro processado pelo Stripe.</span>
            </footer>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Pagamento;